import React, { useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCalendarDay,
  faCalendarWeek,
  faClock,
  faList,
  faSquare,
  faSquareCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { Tab, Tabs, TabsList } from '@buildoutinc/blueprint-react/ui/Tabs';
import moment from 'moment';

import { ISO_DATE_FORMAT } from 'helpers/formatters/date_time_formats';
import { getTaskCounts } from '../helpers';
import { useTasks } from '../context/tasks_context';

import type { Filters } from '../filters/types';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { TaskCounts } from '../helpers';

type StatusKey = 'all' | 'incomplete' | 'overdue' | 'completed';
type DueDateKey = 'today' | 'week' | 'month';
type StatusCountKey = Exclude<StatusKey, 'all'>;

const SHOW_ALL_VALUE = 'show-all';

interface StatusOption {
  key: StatusCountKey;
  label: string;
  icon: IconDefinition;
}

interface DueDateOption {
  key: DueDateKey;
  label: string;
  icon: IconDefinition;
}

const STATUS_OPTIONS: StatusOption[] = [
  { key: 'incomplete', label: 'Incomplete', icon: faSquare },
  { key: 'overdue', label: 'Overdue', icon: faClock },
  { key: 'completed', label: 'Completed', icon: faSquareCheck },
];

const DUE_DATE_OPTIONS: DueDateOption[] = [
  { key: 'today', label: 'Due Today', icon: faCalendarDay },
  { key: 'week', label: 'Upcoming Week', icon: faCalendarWeek },
  { key: 'month', label: 'Upcoming 30 Days', icon: faCalendar },
];

function statusKeyFromFilters({ completed, open, overdue }: Filters): StatusKey {
  if (completed && !open && !overdue) return 'completed';
  if (overdue && !completed && !open) return 'overdue';
  if (open && overdue && !completed) return 'incomplete';
  return 'all';
}

function dueDateKeyFromFilters({ fromDate, toDate }: Filters): DueDateKey | null {
  if (!fromDate || !toDate) return null;
  const today = moment().format(ISO_DATE_FORMAT);
  if (fromDate !== today) return null;
  if (toDate === today) return 'today';
  if (toDate === moment().add(7, 'days').format(ISO_DATE_FORMAT)) return 'week';
  if (toDate === moment().add(30, 'days').format(ISO_DATE_FORMAT)) return 'month';
  return null;
}

function statusChanges(key: StatusKey): Partial<Filters> {
  switch (key) {
  case 'incomplete':
    return { completed: false, open: true, overdue: true };
  case 'overdue':
    return { completed: false, open: false, overdue: true };
  case 'completed':
    return { completed: true, open: false, overdue: false };
  case 'all':
  default:
    return { completed: false, open: false, overdue: false };
  }
}

function dueDateChanges(key: DueDateKey): Partial<Filters> {
  const today = moment().format(ISO_DATE_FORMAT);
  switch (key) {
  case 'today':
    return { fromDate: today, toDate: today };
  case 'week':
    return { fromDate: today, toDate: moment().add(7, 'days').format(ISO_DATE_FORMAT) };
  case 'month':
    return { fromDate: today, toDate: moment().add(30, 'days').format(ISO_DATE_FORMAT) };
  }
}

function formatCount(value: number | undefined): string | null {
  if (value === undefined) return null;
  if (value > 99) return '99+';
  return String(value);
}

interface CountBadgeProps {
  counts: TaskCounts | null;
  countsLoading: boolean;
  countKey: keyof TaskCounts;
}

function CountBadge({ counts, countsLoading, countKey }: CountBadgeProps) {
  if (!counts && !countsLoading) return null;
  const label = counts ? formatCount(counts[countKey]) : null;

  return (
    <Badge variant="secondary">
      {countsLoading && !counts ? '…' : label}
    </Badge>
  );
}

export function TasksQuickFilters() {
  const { filters, changeFilters, paths } = useTasks();
  const [counts, setCounts] = useState<TaskCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);

  const countsPath = paths.counts;

  const debouncedLoadCounts = useMemo(
    () => debounce(async (filters: Filters) => {
      if (!countsPath) return;

      setCountsLoading(true);
      try {
        const data = await getTaskCounts({
          path: countsPath,
          filters: {
            allTasks: filters.allTasks,
            assigneeId: filters.assigneeId,
            dealUserId: filters.dealUserId,
            officeId: filters.officeId,
            parentTypeFilter: filters.parentType,
            query: filters.query,
            tbd: filters.tbd,
          },
        });
        if (data) {
          setCounts(data);
        }
      } finally {
        setCountsLoading(false);
      }
    }, 500),
    [countsPath]
  );

  useEffect(() => {
    if (!countsPath) return;
    debouncedLoadCounts(filters);
  }, [
    countsPath,
    debouncedLoadCounts,
    filters.allTasks,
    filters.assigneeId,
    filters.dealUserId,
    filters.officeId,
    filters.parentType,
    filters.query,
    filters.tbd,
  ]);

  const activeStatus = useMemo(() => statusKeyFromFilters(filters), [filters]);
  const activeDueDate = useMemo(() => dueDateKeyFromFilters(filters), [filters]);
  const showAllActive = activeStatus === 'all' && activeDueDate === null;

  function selectStatus(key: StatusKey) {
    const next: StatusKey = activeStatus === key ? 'all' : key;
    const changes: Partial<Filters> = statusChanges(next);
    // Overdue (deadline < today) can never intersect with a Due Date preset (deadline >= today),
    // so clear the date filter when Overdue is picked.
    if (next === 'overdue') {
      changes.fromDate = null;
      changes.toDate = null;
    }
    changeFilters(changes);
  }

  function selectDueDate(key: DueDateKey) {
    if (activeDueDate === key) {
      changeFilters({ fromDate: null, toDate: null });
    } else {
      changeFilters(dueDateChanges(key));
    }
  }

  function showAll() {
    if (showAllActive) return;
    changeFilters({ ...statusChanges('all'), fromDate: null, toDate: null });
  }

  return (
    <div className="d-flex flex-column gap-4" style={{ minWidth: '220px' }}>
      <Tabs orientation="vertical" value={showAllActive ? SHOW_ALL_VALUE : null}>
        <TabsList orientation="vertical" variant="pills">
          <Tab
            icon={<FontAwesomeIcon icon={faList} />}
            value={SHOW_ALL_VALUE}
            onClick={showAll}
          >
            Show All
            <CountBadge countKey="total" counts={counts} countsLoading={countsLoading} />
          </Tab>
        </TabsList>
      </Tabs>

      <div className="d-flex flex-column gap-2">
        <div className="fs-large fw-semibold lh-large">Status</div>
        <Tabs orientation="vertical" value={activeStatus === 'all' ? null : activeStatus}>
          <TabsList orientation="vertical" variant="pills">
            {STATUS_OPTIONS.map(option => (
              <Tab
                icon={<FontAwesomeIcon icon={option.icon} />}
                key={option.key}
                value={option.key}
                onClick={() => selectStatus(option.key)}
              >
                {option.label}
                <CountBadge countKey={option.key} counts={counts} countsLoading={countsLoading} />
              </Tab>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="d-flex flex-column gap-2">
        <div className="fs-large fw-semibold lh-large">Due Date</div>
        <Tabs orientation="vertical" value={activeDueDate}>
          <TabsList orientation="vertical" variant="pills">
            {DUE_DATE_OPTIONS.map(option => (
              <Tab
                icon={<FontAwesomeIcon icon={option.icon} />}
                key={option.key}
                value={option.key}
                onClick={() => selectDueDate(option.key)}
              >
                {option.label}
                <CountBadge countKey={option.key} counts={counts} countsLoading={countsLoading} />
              </Tab>
            ))}
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}
